import { NTSTemplateDefinition } from '../types/template';
import { v4 as uuidv4 } from 'uuid';

// UUID cache to maintain consistent UUIDs for the same template ID
const uuidCache: Record<string, string> = {};

// Default template ID constant - change this single line to update the default everywhere
export const DEFAULT_TEMPLATE_ID = 'enhanced-professional';

/**
 * Default template definition - Classic Professional
 */
export const defaultTemplate: NTSTemplateDefinition = {
  id: 'default',
  name: 'Classic Professional',
  description: 'A clean, professional template with traditional layout',
  version: '1.0.0',
  category: 'Professional',
  tags: ['classic', 'professional', 'traditional'],
  previewImage: '/lovable-uploads/f8e62a83-a6ce-4e8c-8ab5-4615528fd13c.png',
  style: {
    colors: {
      primary: '#1612d3',      // Application blue
      secondary: '#475569',
      text: '#1A1F2C',
      background: '#ffffff',
      accent: '#1612d3',       // Application blue accent
      heading: '#1612d3',      // Match primary blue
      muted: '#64748B',
      border: '#e2e8f0'
    },
    typography: {
      fontFamily: "'Inter', system-ui, sans-serif",
      headingFont: "'Inter', system-ui, sans-serif",
      fontSize: "var(--nts-font-size, 14px)",
      lineHeight: '1.6',       // Increased for better readability
      letterSpacing: 'normal',
      fontWeight: 'normal'
    },
    spacing: {
      section: '32px',         // Increased from 24px
      item: '18px',            // Increased from 16px
      container: '48px',
      sectionGap: '36px',      // Increased from 32px
      itemGap: '14px',         // Increased from 12px
      containerPadding: '28px' // Increased from 24px
    },
    layout: {
      columns: 1,
      headerStyle: 'standard',
      contentWidth: '100%',
      sectionGap: '36px'       // Increased from 32px
    }
  }
};

/**
 * Modern template definition
 */
export const modernTemplate: NTSTemplateDefinition = {
  id: 'modern',
  name: 'Modern',
  description: 'A bold, contemporary design with sidebar layout',
  version: '1.0.0',
  category: 'Creative',
  tags: ['modern', 'creative', 'sidebar'],
  previewImage: '/lovable-uploads/b54b7470-1323-4c57-8d7b-503c53c6e7dc.png',
  style: {
    colors: {
      primary: '#111827',
      secondary: '#4B5563',
      text: '#1F2937',
      background: '#ffffff',
      accent: '#2563EB',
      heading: '#111827',
      muted: '#6B7280',
      border: '#E5E7EB'
    },
    typography: {
      fontFamily: "'Inter', system-ui, sans-serif",
      headingFont: "'Inter', system-ui, sans-serif",
      fontSize: "var(--nts-font-size, 14px)",
      lineHeight: '1.5',
      letterSpacing: 'normal',
      fontWeight: 'normal'
    },
    spacing: {
      section: '24px',
      item: '16px',
      container: '48px',
      sectionGap: '28px',
      itemGap: '12px',
      containerPadding: '24px'
    },
    layout: {
      columns: 2,
      headerStyle: 'sidebar',
      contentWidth: '100%',
      sectionGap: '28px'
    }
  }
};

/**
 * Minimalist template definition
 */
export const minimalistTemplate: NTSTemplateDefinition = {
  id: 'minimalist',
  name: 'Minimalist',
  description: 'A clean, minimal design that focuses on content',
  version: '1.0.0',
  category: 'Simple',
  tags: ['minimal', 'simple', 'clean'],
  previewImage: '/lovable-uploads/d58f4101-b005-4d22-a3d1-563c1dcf7131.png',
  style: {
    colors: {
      primary: '#18181B',
      secondary: '#71717A',
      text: '#27272A',
      background: '#ffffff',
      accent: '#3B82F6',
      heading: '#18181B',
      muted: '#A1A1AA',
      border: '#E4E4E7'
    },
    typography: {
      fontFamily: "'Inter', system-ui, sans-serif",
      headingFont: "'Inter', system-ui, sans-serif",
      fontSize: "var(--nts-font-size, 14px)",
      lineHeight: '1.4',
      letterSpacing: 'normal',
      fontWeight: 'normal'
    },
    spacing: {
      section: '20px',
      item: '14px',
      container: '32px',
      sectionGap: '24px',
      itemGap: '10px',
      containerPadding: '24px'
    },
    layout: {
      columns: 1,
      headerStyle: 'minimal',
      contentWidth: '100%',
      sectionGap: '24px'
    }
  }
};

/**
 * Business Teal template definition
 */
export const businessTealTemplate: NTSTemplateDefinition = {
  id: 'business-teal',
  name: 'Business Teal',
  description: 'A professional template with a teal sidebar for executives and managers',
  version: '1.0.0',
  category: 'Professional',
  tags: ['business', 'professional', 'executive', 'teal', 'sidebar'],
  previewImage: '/lovable-uploads/8513b58d-1aa0-4cf5-9d07-6785747c18d6.png',
  style: {
    colors: {
      primary: '#333333',
      secondary: '#777777',
      text: '#333333',
      background: '#ffffff',
      accent: '#006666',
      heading: '#333333',
      muted: '#999999',
      border: '#e2e8f0'
    },
    typography: {
      fontFamily: "'Inter', system-ui, sans-serif",
      headingFont: "'Inter', system-ui, sans-serif",
      fontSize: "var(--nts-font-size, 14px)",
      lineHeight: '1.5',
      letterSpacing: 'normal',
      fontWeight: 'normal'
    },
    spacing: {
      section: '24px',
      item: '16px',
      container: '48px',
      sectionGap: '28px',
      itemGap: '12px',
      containerPadding: '24px'
    },
    layout: {
      columns: 2,
      headerStyle: 'sidebar',
      contentWidth: '100%',
      sectionGap: '28px'
    }
  }
};

/**
 * Enhanced Professional template definition
 */
export const enhancedProfessionalTemplate: NTSTemplateDefinition = {
  id: 'enhanced-professional',
  name: 'Enhanced Professional',
  description: 'A modern professional template with sidebar and photo section',
  version: '1.0.0',
  category: 'Professional',
  tags: ['professional', 'modern', 'sidebar', 'photo'],
  previewImage: '/lovable-uploads/792fb0ec-a351-4d27-b62f-ee47e02499eb.png',
  style: {
    colors: {
      primary: '#333333',
      secondary: '#666666',
      text: '#333333',
      background: '#ffffff',
      accent: '#0096CF', // Blue accent color
      heading: '#333333',
      muted: '#777777',
      border: '#e2e8f0',
      sidebar: '#1A3A4D' // Dark blue sidebar
    },
    typography: {
      fontFamily: "'Inter', system-ui, sans-serif",
      headingFont: "'Inter', system-ui, sans-serif",
      fontSize: "var(--nts-font-size, 14px)",
      lineHeight: '1.5',
      letterSpacing: 'normal',
      fontWeight: 'normal'
    },
    spacing: {
      section: '24px',
      item: '16px',
      container: '48px',
      sectionGap: '28px',
      itemGap: '12px',
      containerPadding: '24px'
    },
    layout: {
      columns: 2,
      headerStyle: 'modern',
      contentWidth: '100%',
      sectionGap: '28px'
    }
  }
};

// Registry of all available templates - FIXED: Remove duplicate mappings
const templateRegistry: Record<string, NTSTemplateDefinition> = {
  default: defaultTemplate,
  modern: modernTemplate,
  minimalist: minimalistTemplate,
  'business-teal': businessTealTemplate,
  'enhanced-professional': enhancedProfessionalTemplate,
};

/**
 * Get template definition by ID
 * Handles UUID format by correctly identifying compound IDs and aliases
 * 
 * @param id - Template ID or UUID
 * @returns The template definition or default template if not found
 */
export const getTemplateById = (id: string): NTSTemplateDefinition => {
  // Enhanced template ID resolution with logging
  console.log(`getTemplateById called with ID: ${id}`);
  
  // Direct lookup first
  if (templateRegistry[id]) {
    console.log(`Direct match found for ${id}: ${templateRegistry[id].name}`);
    return templateRegistry[id];
  }
  
  // Extract base template ID for known compound IDs and aliases
  let baseId = id;
  
  // Handle aliases and prefixes
  if (id.startsWith('business-') || id.startsWith('business_') || id === 'business') {
    console.log('Business template detected, mapping to business-teal');
    baseId = 'business-teal';
  } 
  else if (id.startsWith('enhanced-') || id.startsWith('enhanced_') || id === 'enhanced' || id === 'professional') {
    console.log('Enhanced Professional template detected, mapping to enhanced-professional');
    baseId = 'enhanced-professional';
  }
  // Standard UUID format with hyphen
  else if (id.includes('-')) {
    const firstPart = id.split('-')[0];
    console.log(`Extracted base ID from UUID: ${firstPart}`);
    
    // Map known base IDs to full template IDs
    if (firstPart === 'enhanced') {
      baseId = 'enhanced-professional';
      console.log(`Mapped 'enhanced' prefix to enhanced-professional`);
    } else if (firstPart === 'business') {
      baseId = 'business-teal';
      console.log(`Mapped 'business' prefix to business-teal`);
    } else {
      baseId = firstPart;
    }
  }
  
  const template = templateRegistry[baseId];
  if (!template) {
    console.warn(`Template with ID ${id} (base: ${baseId}) not found, falling back to default template`);
    return defaultTemplate;
  }
  
  console.log(`Found template for base ID ${baseId}: ${template.name}`);
  return template;
};

/**
 * Get all templates as array
 * @returns Array of all unique template definitions
 */
export const getAllTemplates = (): NTSTemplateDefinition[] => {
  // Return only the unique template objects from the registry
  return Object.values(templateRegistry);
};

/**
 * Convert template ID to UUID format
 * Ensures consistent UUIDs for the same template ID
 * 
 * @param id - Template ID
 * @returns UUID format of the template ID
 */
export const getTemplateUUID = (id: string): string => {
  // If it's already a valid UUID, return it as is
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    return id;
  }
  
  // Check if we already have a UUID for this template ID
  if (!uuidCache[id]) {
    // Use the base template ID as part of the UUID for consistency
    let baseId = id.split('-')[0];
    
    // Handle compound template IDs for UUID generation
    if (baseId === 'business') {
      baseId = 'business-teal';
    } else if (baseId === 'enhanced') {
      baseId = 'enhanced-professional';
    }
    
    // Create a deterministic UUID based on the template ID
    uuidCache[id] = `${baseId}-${uuidv4().substring(baseId.length + 1)}`;
  }
  
  return uuidCache[id];
};
